/* MoRetro Studio V27 - Workshop UI/rendering. */
window.MTS = window.MTS || {};

(function(MTS){
  "use strict";

  const cfg = MTS.WorkshopConfig;
  const st = cfg.state;
  const h = cfg.h;

  function machineTelemetry(machine = {}){
    return machine.telemetry || machine;
  }

  function connectionLabel(type, materialSystem){
    return {
      klipper:"Klipper / Moonraker",
      moonraker:"Klipper / Moonraker",
      bambu:"Bambu Lab",
      bambu_ams:"Bambu Lab + AMS",
      anycubic:"Anycubic",
      anycubic_ace:"Anycubic + ACE Pro",
      creality:"Creality / Sonic Pad",
      sonicpad:"Creality / Sonic Pad",
      octoprint:"OctoPrint",
      other:"Autre"
    }[type] || (materialSystem === "ams" ? "Bambu Lab + AMS" : materialSystem === "ace-pro" ? "Anycubic + ACE Pro" : "Autre");
  }

  function connectionDot(machine = {}){
    const t = machineTelemetry(machine);
    if(t.online === true) return "Connector OK";
    if(t.online === false && t.state === "offline") return "Connector OFF";
    const status = machine.lastConnectionStatus || "unknown";
    if(status === "online") return "Connectee";
    if(status === "offline") return "Hors ligne";
    if(status === "testing") return "Test...";
    return "Non testee";
  }

  function preferredIp(machine = {}){
    return String(machine.localIp || machine.ip || machine.tailscaleIp || "").trim();
  }

  function defaultInterfaceUrl(machine = {}){
    const ip = preferredIp(machine);
    if(!ip) return "";
    if(machine.interfaceUrl) return machine.interfaceUrl;
    const port = String(machine.port || "").trim();
    if(port) return `http://${ip}:${port}`;
    return `http://${ip}`;
  }

  function connectionTestUrl(machine = {}){
    const ip = preferredIp(machine);
    if(!ip) return "";
    if(machine.connectionType === "klipper" || machine.connectionType === "moonraker"){
      const port = String(machine.port || "7125").trim();
      return `http://${ip}:${port}/printer/info`;
    }
    return defaultInterfaceUrl(machine);
  }

  function studioProxyUrl(machine = {}){
    return String(machine.proxyUrl || "").trim();
  }

  function absoluteApiUrl(path){
    const base = String(cfg.API_BASE || "").replace(/\/$/, "");
    if(!path) return "";
    if(/^https?:\/\//i.test(path)) return path;
    return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
  }

  function relayCameraUrl(machine = {}){
    const id = String(machine.id || "").trim();
    const relay = machine.cameraRelayUrl || machine.telemetry?.cameraRelayUrl;
    if(relay) return absoluteApiUrl(relay);
    if(machine.cameraRelay || machine.telemetry?.cameraRelay){
      return absoluteApiUrl(`/api/connector/camera/${encodeURIComponent(id)}.mjpg`);
    }
    return "";
  }

  function relayStillCameraUrl(machine = {}){
    const id = String(machine.id || "").trim();
    const still = machine.cameraStillUrl || machine.telemetry?.cameraStillUrl;
    if(still) return absoluteApiUrl(still);
    const relay = relayCameraUrl(machine);
    if(relay) return relay.replace(/\.mjpg(\?|$)/, ".jpg$1").replace(/\.jpg(\?|$)/, ".jpg$1");
    if(machine.cameraRelay || machine.telemetry?.cameraRelay){
      return absoluteApiUrl(`/api/connector/camera/${encodeURIComponent(id)}.jpg`);
    }
    return "";
  }

  function cameraUrl(machine = {}){
    const local = String(machine.localCameraUrl || machine.telemetry?.localCameraUrl || "").trim();
    if(local) return local;
    const id = String(machine.id || "").trim();
    const connectorSeen = machine.telemetry || machine.lastSeenAt || machine.connectorId;
    if(id && connectorSeen){
      return `http://127.0.0.1:17827/camera/${encodeURIComponent(id)}.jpg`;
    }
    const relay = relayCameraUrl(machine);
    if(relay) return relay;
    const cached = st.cameraDetections.get(machine.id);
    if(cached?.ok && cached.url) return cached.url;
    return String(machine.webcamUrl || "").trim();
  }

  function directCameraUrl(machine = {}){
    const cached = st.cameraDetections.get(machine.id);
    if(cached?.ok && cached.url) return cached.url;
    return String(machine.webcamUrl || "").trim();
  }

  function cameraCandidates(machine = {}){
    const ip = preferredIp(machine);
    const saved = String(machine.webcamUrl || "").trim();
    if(!ip && !saved) return [];
    const list = [];
    if(saved) list.push(saved);
    if(ip){
      [
        `http://${ip}/webcam/?action=stream`,
        `http://${ip}:8080/?action=stream`,
        `http://${ip}:8081/?action=stream`,
        `http://${ip}:8080/stream`,
        `http://${ip}:8081/stream`,
        `http://${ip}/stream`,
        `http://${ip}/video`,
        `http://${ip}/?action=stream`,
        `http://${ip}:8080/snapshot`,
        `http://${ip}:8081/snapshot`,
        `http://${ip}/snapshot`
      ].forEach(url=>list.push(url));
    }
    return [...new Set(list)];
  }

  function testCameraImage(url, timeoutMs = 2200){
    return new Promise(resolve=>{
      const img = new Image();
      const done = ok=>{
        clearTimeout(timer);
        img.onload = null;
        img.onerror = null;
        resolve(ok);
      };
      const timer = setTimeout(()=>done(false), timeoutMs);
      img.onload = ()=>done(true);
      img.onerror = ()=>done(false);
      img.src = `${url}${url.includes("?") ? "&" : "?"}_mts=${Date.now()}`;
    });
  }

  async function detectCamera(machine = {}){
    const id = machine.id;
    if(!id || st.cameraDetectionPending.has(id)) return;
    const existing = st.cameraDetections.get(id);
    if(existing && Date.now() - existing.checkedAt < 120000) return;
    const candidates = cameraCandidates(machine);
    if(!candidates.length) return;

    st.cameraDetectionPending.add(id);
    st.cameraDetections.set(id, {ok:false, checkedAt:Date.now(), testing:true});

    for(const url of candidates){
      if(await testCameraImage(url)){
        st.cameraDetections.set(id, {ok:true, url, checkedAt:Date.now()});
        st.cameraDetectionPending.delete(id);
        MTS.WorkshopMachines.scheduleRender();
        return;
      }
    }

    st.cameraDetections.set(id, {ok:false, checkedAt:Date.now()});
    st.cameraDetectionPending.delete(id);
  }

  function cameraButton(machine = {}){
    const url = cameraUrl(machine);
    const pending = st.cameraDetectionPending.has(machine.id);
    if(url) return `<button type="button" class="mts-camera-btn" data-machine-camera-open="${h.esc(machine.id)}" data-camera-url="${h.esc(url)}">Camera${relayCameraUrl(machine) ? " relay" : ""}</button>`;
    return pending ? `<span class="mts-camera-btn muted">Camera...</span>` : "";
  }

  function openCameraPopup(machineId, url){
    if(!url) return;
    closeCameraPopup();
    const machine = cfg.state.machines.find(m=>m.id === machineId) || {};
    const directUrl = directCameraUrl(machine);
    const isServerRelay = url.includes("/api/connector/camera/");
    const isLocalRelay = /^http:\/\/(?:127\.0\.0\.1|localhost):17827\/camera\//i.test(url);
    const isRelay = isServerRelay || isLocalRelay;
    const streamUrl = isServerRelay ? url.replace(/\.jpg(\?|$)/, ".mjpg$1") : url;
    const stillUrl = isLocalRelay
      ? url
      : isServerRelay
        ? relayStillCameraUrl(machine) || url.replace(/\.mjpg(\?|$)/, ".jpg$1")
        : url;
    const displayUrl = isRelay
      ? `${stillUrl}${stillUrl.includes("?") ? "&" : "?"}t=${Date.now()}`
      : streamUrl;
    const modal = document.createElement("div");
    modal.id = "mtsCameraModal";
    modal.className = "mts-camera-modal";
    modal.innerHTML = `
      <div class="mts-camera-dialog" role="dialog" aria-modal="true" aria-label="Camera machine">
        <div class="mts-camera-dialog-head">
          <div>
            <b>${h.esc(machine.name || "Camera")}</b>
            <span>${h.esc(streamUrl)}</span>
          </div>
          <button type="button" data-camera-close aria-label="Fermer">Fermer</button>
        </div>
        <div class="mts-camera-frame">
          <img data-camera-relay-img="${isRelay ? "1" : "0"}" data-camera-still-url="${h.esc(stillUrl)}" src="${h.esc(displayUrl)}" alt="Flux camera ${h.esc(machine.name || "")}" referrerpolicy="no-referrer">
          <small data-camera-status>${isLocalRelay ? "Copie locale du flux via MoRetro Connector" : isRelay ? "Image relay actualisee automatiquement" : "Flux camera"}</small>
        </div>
        <div class="mts-camera-dialog-foot">
          <a href="${h.esc(streamUrl)}" target="_blank" rel="noopener">Ouvrir le flux</a>
          ${isServerRelay ? `<a href="${h.esc(stillUrl)}" target="_blank" rel="noopener">Image directe</a>` : ""}
          ${directUrl && directUrl !== url ? `<a href="${h.esc(directUrl)}" target="_blank" rel="noopener">Source locale</a>` : ""}
        </div>
      </div>
    `;
    modal.addEventListener("click", e=>{
      if(e.target === modal || e.target.closest?.("[data-camera-close]")) closeCameraPopup();
    });
    document.body.appendChild(modal);
    if(isRelay){
      const img = modal.querySelector("[data-camera-relay-img]");
      const status = modal.querySelector("[data-camera-status]");
      img.onerror = ()=>{
        if(status) status.textContent = "Image relay momentanement indisponible";
      };
      img.onload = ()=>{
        if(status) status.textContent = `${isLocalRelay ? "Copie locale actualisee" : "Image relay actualisee"} - ${new Date().toLocaleTimeString("fr-FR")}`;
      };
      st.cameraPopupRefreshTimer = setInterval(()=>{
        if(!document.body.contains(modal)) return closeCameraPopup();
        img.src = `${stillUrl}${stillUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;
      }, 1000);
    }
  }

  function closeCameraPopup(){
    if(st.cameraPopupRefreshTimer){
      clearInterval(st.cameraPopupRefreshTimer);
      st.cameraPopupRefreshTimer = null;
    }
    document.getElementById("mtsCameraModal")?.remove();
  }

  function telemetryLine(machine = {}){
    const t = machineTelemetry(machine);
    if(!t) return "";
    const parts = [];
    if(t.state) parts.push(`Etat ${t.state}`);
    if(t.filename) parts.push(String(t.filename).split("/").pop());
    if(Number.isFinite(Number(t.progress))) parts.push(`${Number(t.progress).toFixed(1).replace(".", ",")}%`);
    const nozzleTemp = t.nozzleTemp ?? t.extruderTemp;
    const nozzleTarget = t.nozzleTarget ?? t.extruderTarget;
    if(Number.isFinite(Number(nozzleTemp))) parts.push(`Buse ${Math.round(Number(nozzleTemp))}/${Math.round(Number(nozzleTarget || 0))}C`);
    if(Number.isFinite(Number(t.bedTemp))) parts.push(`Plateau ${Math.round(Number(t.bedTemp))}/${Math.round(Number(t.bedTarget || 0))}C`);
    return parts.length ? parts.join(" - ") : "";
  }

  function lastSeenLabel(machine = {}){
    const d = h.toDate(machine.lastSeenAt || machine.telemetry?.updatedAt || machine.updatedAt);
    if(!d) return "";
    const diff = Date.now() - d.getTime();
    const sec = Math.max(0, Math.round(diff / 1000));
    if(sec < 60) return "a l'instant";
    const min = Math.round(sec / 60);
    if(min < 60) return `il y a ${min} min`;
    const hrs = Math.round(min / 60);
    return `il y a ${hrs} h`;
  }

  function telemetryPanel(machine = {}){
    const t = machineTelemetry(machine);
    const hasTelemetry = t && (t.online === true || t.online === false || t.nozzleTemp != null || t.bedTemp != null || t.progress != null || t.error);
    if(!hasTelemetry){
      return `<div class="mts-connector-box muted"><b>Connector</b><span>Aucune donnee recue pour cette machine.</span></div>`;
    }

    const online = t.online === true;
    const progress = t.progress == null ? "-" : `${Number(t.progress).toFixed(1).replace(".", ",")}%`;
    const nozzleTemp = t.nozzleTemp ?? t.extruderTemp;
    const nozzleTarget = t.nozzleTarget ?? t.extruderTarget;
    const nozzle = nozzleTemp == null ? "-" : `${Math.round(Number(nozzleTemp))} / ${Math.round(Number(nozzleTarget || 0))} C`;
    const bed = t.bedTemp == null ? "-" : `${Math.round(Number(t.bedTemp))} / ${Math.round(Number(t.bedTarget || 0))} C`;
    const msg = t.message || t.error || "Derniere synchronisation Connector";

    return `
      <div class="mts-connector-box ${online ? "online" : "offline"}">
        <div class="mts-connector-head">
          <b>${online ? "Connector en ligne" : "Connector hors ligne"}</b>
          <span>${h.esc(lastSeenLabel(machine) || "")}</span>
        </div>
        <small>${h.esc(msg)}</small>
        <div class="mts-connector-grid">
          <span>${h.esc(progress)}</span>
          <span>Buse ${h.esc(nozzle)}</span>
          <span>Plateau ${h.esc(bed)}</span>
        </div>
      </div>
    `;
  }

  function manualStatusButtons(machine = {}, project){
    const status = machine.manualStatus || "";
    const id = h.esc(machine.id);
    const disabledOccupied = project ? "disabled" : "";
    return `
      <div class="mts-workshop-status-actions" aria-label="Etat manuel machine">
        <button type="button" class="${!status ? "active" : ""}" data-machine-status="${id}" data-status-value="" ${disabledOccupied}>Libre</button>
        <button type="button" class="${status === "maintenance" ? "active danger" : ""}" data-machine-status="${id}" data-status-value="maintenance">Maintenance</button>
        <button type="button" class="${status === "offline" ? "active danger" : ""}" data-machine-status="${id}" data-status-value="offline">Hors ligne</button>
      </div>
    `;
  }

  function machineFormValue(machine, field){
    const draft = st.machineDrafts.get(machine.id) || {};
    if(Object.prototype.hasOwnProperty.call(draft, field)) return draft[field];
    if(field === "localIp") return machine.localIp || machine.ip || machine.host || "";
    if(field === "connectionType") return machine.connectionType || machine.type || "other";
    if(field === "proxyUrl") return studioProxyUrl(machine);
    return machine[field] || "";
  }

  function updateMachineDraft(machineId, patch = {}){
    const id = h.uidSafe(machineId);
    const current = st.machineDrafts.get(id) || {};
    st.machineDrafts.set(id, {...current, ...patch});
  }

  function collectVisibleDrafts(box){
    if(!box) return;
    box.querySelectorAll("[data-machine-id]").forEach(card=>{
      const id = card.dataset.machineId;
      if(!id) return;
      const patch = collectMachinePatch(card);
      if(Object.keys(patch).length) updateMachineDraft(id, patch);
    });
  }

  function connectionPanel(machine = {}){
    const id = h.esc(machine.id);
    const formMachine = {
      ...machine,
      localIp: machineFormValue(machine, "localIp"),
      ip: machineFormValue(machine, "localIp"),
      host: machineFormValue(machine, "localIp"),
      tailscaleIp: machineFormValue(machine, "tailscaleIp"),
      mac: machineFormValue(machine, "mac"),
      connectionType: machineFormValue(machine, "connectionType"),
      type: machineFormValue(machine, "connectionType"),
      materialSystem: machineFormValue(machine, "materialSystem"),
      port: machineFormValue(machine, "port"),
      interfaceUrl: machineFormValue(machine, "interfaceUrl"),
      webcamUrl: machineFormValue(machine, "webcamUrl"),
      proxyUrl: machineFormValue(machine, "proxyUrl")
    };
    const interfaceUrl = defaultInterfaceUrl(formMachine);
    const proxyUrl = studioProxyUrl(formMachine);

    return `
      <details class="mts-machine-network" data-network-details="${id}" ${st.openNetworkPanels.has(machine.id) ? "open" : ""}>
        <summary>Connexion reseau</summary>
        <div class="mts-machine-network-grid">
          <label>IP locale
            <input type="text" value="${h.esc(formMachine.localIp || formMachine.ip || "")}" placeholder="192.168.1.42" data-machine-field="localIp">
          </label>
          <label>IP Tailscale
            <input type="text" value="${h.esc(formMachine.tailscaleIp || "")}" placeholder="100.x.x.x" data-machine-field="tailscaleIp">
          </label>
          <label>Adresse MAC
            <input type="text" value="${h.esc(formMachine.mac || "")}" placeholder="AA:BB:CC:DD:EE:FF" data-machine-field="mac">
          </label>
          <label>Type
            <select data-machine-field="connectionType">
              <option value="klipper" ${formMachine.connectionType === "klipper" ? "selected" : ""}>Klipper / Moonraker</option>
              <option value="bambu" ${formMachine.connectionType === "bambu" ? "selected" : ""}>Bambu Lab</option>
              <option value="bambu_ams" ${formMachine.connectionType === "bambu_ams" ? "selected" : ""}>Bambu Lab + AMS</option>
              <option value="anycubic" ${formMachine.connectionType === "anycubic" ? "selected" : ""}>Anycubic</option>
              <option value="anycubic_ace" ${formMachine.connectionType === "anycubic_ace" ? "selected" : ""}>Anycubic + ACE Pro</option>
              <option value="creality" ${formMachine.connectionType === "creality" ? "selected" : ""}>Creality / Sonic Pad</option>
              <option value="other" ${!formMachine.connectionType || formMachine.connectionType === "other" ? "selected" : ""}>Autre</option>
            </select>
          </label>
          <label>Systeme matiere
            <select data-machine-field="materialSystem">
              <option value="" ${!formMachine.materialSystem ? "selected" : ""}>Aucun</option>
              <option value="ams" ${formMachine.materialSystem === "ams" ? "selected" : ""}>AMS</option>
              <option value="ace-pro" ${formMachine.materialSystem === "ace-pro" ? "selected" : ""}>ACE Pro</option>
            </select>
          </label>
          <label>Port
            <input type="text" value="${h.esc(formMachine.port || "")}" placeholder="7125" data-machine-field="port">
          </label>
          <label>URL interface
            <input type="text" value="${h.esc(formMachine.interfaceUrl || "")}" placeholder="${h.esc(interfaceUrl || "http://...")}" data-machine-field="interfaceUrl">
          </label>
          <label>URL tunnel / proxy HTTPS
            <input type="text" value="${h.esc(proxyUrl || "")}" placeholder="https://mon-imprimante.exemple.com" data-machine-field="proxyUrl">
          </label>
          <label>URL camera
            <input type="text" value="${h.esc(formMachine.webcamUrl || directCameraUrl(machine) || "")}" placeholder="http://ip:8080/?action=stream" data-machine-field="webcamUrl">
          </label>
        </div>
        <div class="mts-machine-network-actions">
          <button type="button" class="mts-btn mts-soft" data-machine-save="${id}">Enregistrer</button>
          <button type="button" class="mts-btn mts-soft" data-machine-test="${id}">Tester local</button>
          <button type="button" class="mts-btn mts-soft" data-machine-studio-test="${id}">Actualiser Connector</button>
          <button type="button" class="mts-btn mts-soft" data-machine-camera-detect="${id}">Detecter camera</button>
          ${cameraUrl(machine) ? `<button type="button" class="mts-btn mts-soft" data-machine-camera-open="${id}" data-camera-url="${h.esc(cameraUrl(machine))}">Camera</button>` : ""}
          ${interfaceUrl ? `<a class="mts-btn mts-soft" href="${h.esc(interfaceUrl)}" target="_blank" rel="noopener">Ouvrir</a>` : ""}
        </div>
        <p class="mts-machine-network-status">${h.esc(connectionDot(machine))}${machine.lastConnectionAt ? ` - ${h.esc(new Date(machine.lastConnectionAt).toLocaleString("fr-FR"))}` : ""}${machine.lastConnectionMessage ? `<br>${h.esc(machine.lastConnectionMessage)}` : ""}${machine.lastSeenAt ? `<br>Connector : ${h.esc(lastSeenLabel(machine))}` : ""}${telemetryLine(machine) ? `<br>${h.esc(telemetryLine(machine))}` : ""}</p>
      </details>
    `;
  }

  function card(printer){
    const prod = MTS.WorkshopProduction;
    const project = prod.assignedProject(printer);
    const cls = prod.statusClass(project, printer);
    const tLine = telemetryLine(printer);

    return `
      <article class="mts-workshop-card ${h.esc(cls)}" data-machine-id="${h.esc(printer.id)}">
        <div class="mts-workshop-card-top">
          <div>
            <strong>${h.esc(printer.name || "Imprimante")}</strong>
            <span>${h.esc([printer.brand, printer.model].filter(Boolean).join(" ") || "Atelier MoRetro")}</span>
          </div>
          <em>${h.esc(prod.statusLabel(project, printer))}</em>
        </div>

        ${project ? `
          <div class="mts-workshop-project">
            <small>Projet en cours</small>
            <b>${h.esc(project.projectType || project.title || "Projet")}</b>
            <span>${h.esc(project.name || project.customerName || "Client")} - ${h.money(prod.projectPrice(project))}</span>
          </div>
          <div class="mts-workshop-meta">
            <span>${h.esc(prod.filamentName(project))}</span>
            <span>${h.esc(prod.printTime(project))}</span>
          </div>
        ` : `
          <div class="mts-workshop-free">
            <b>Disponible</b>
            <span>Aucun projet actif assigne.</span>
          </div>
        `}

        <div class="mts-workshop-meta compact">
          <span>${h.esc(connectionLabel(printer.connectionType || printer.type, printer.materialSystem))}</span>
          <span>${h.esc(connectionDot(printer))}</span>
          ${tLine ? `<span>${h.esc(tLine)}</span>` : printer.lastSeenAt ? `<span>Connector ${h.esc(lastSeenLabel(printer))}</span>` : ""}
        </div>

        ${cameraButton(printer)}
        ${manualStatusButtons(printer, project)}
        ${connectionPanel(printer)}
      </article>
    `;
  }

  function ensureStyles(){
    if(document.getElementById("mtsWorkshopNetworkCss")) return;
    const style = document.createElement("style");
    style.id = "mtsWorkshopNetworkCss";
    style.textContent = `
      .mts-workshop-grid-v25{display:grid!important;grid-template-columns:repeat(auto-fit,minmax(240px,1fr))!important;gap:12px!important}
      .mts-workshop-card{min-height:0!important;padding:14px!important;border-radius:14px!important;border:1px solid rgba(255,255,255,.10)!important;background:rgba(255,255,255,.045)!important}
      .mts-workshop-card.free{border-color:rgba(0,200,83,.42)!important;background:linear-gradient(180deg,rgba(0,200,83,.16),rgba(0,0,0,.14))!important}
      .mts-workshop-card.occupied{border-color:rgba(255,153,0,.48)!important;background:linear-gradient(180deg,rgba(255,153,0,.18),rgba(0,0,0,.16))!important}
      .mts-workshop-card.maintenance,.mts-workshop-card.offline{border-color:rgba(255,43,43,.48)!important;background:linear-gradient(180deg,rgba(255,43,43,.18),rgba(0,0,0,.16))!important;box-shadow:0 0 0 1px rgba(255,43,43,.12),0 12px 34px rgba(0,0,0,.22)}
      .mts-workshop-card-top{display:flex!important;justify-content:space-between!important;gap:10px!important;align-items:flex-start!important}
      .mts-workshop-card-top strong{font-size:15px!important;line-height:1.15!important}
      .mts-workshop-card-top span{font-size:11px!important;line-height:1.2!important;color:#bbb!important}
      .mts-workshop-card-top em{font-size:11px!important;border-radius:999px!important;padding:5px 8px!important;white-space:nowrap!important;background:rgba(0,0,0,.22)!important}
      .mts-workshop-project,.mts-workshop-free{margin-top:10px!important;padding:10px!important;border-radius:12px!important;background:rgba(0,0,0,.16)!important}
      .mts-workshop-project small,.mts-workshop-free span{font-size:11px!important}
      .mts-workshop-project b,.mts-workshop-free b{font-size:13px!important;line-height:1.2!important}
      .mts-workshop-meta.compact{display:flex!important;flex-wrap:wrap!important;gap:6px!important;margin-top:9px!important}
      .mts-workshop-meta.compact span{font-size:10px!important;line-height:1.2!important;border-radius:999px!important;padding:5px 7px!important;background:rgba(0,0,0,.20)!important;color:#ddd!important}
      .mts-workshop-status-actions{display:grid!important;grid-template-columns:repeat(3,1fr)!important;gap:6px!important;margin-top:10px!important}
      .mts-workshop-status-actions button{min-width:0!important;border:1px solid rgba(255,255,255,.10)!important;border-radius:10px!important;background:rgba(0,0,0,.22)!important;color:#ddd!important;padding:7px 6px!important;font-size:10px!important;font-weight:900!important;cursor:pointer!important}
      .mts-workshop-status-actions button.active{background:rgba(0,200,83,.20)!important;border-color:rgba(0,200,83,.38)!important;color:#fff!important}
      .mts-workshop-status-actions button.danger,.mts-workshop-status-actions button.active.danger{background:rgba(255,43,43,.18)!important;border-color:rgba(255,43,43,.38)!important;color:#fff!important}
      .mts-workshop-status-actions button:disabled{opacity:.38!important;cursor:not-allowed!important}
      .mts-camera-btn{display:flex!important;align-items:center!important;justify-content:center!important;margin-top:10px!important;border-radius:10px!important;padding:8px 10px!important;background:rgba(40,145,255,.18)!important;border:1px solid rgba(40,145,255,.38)!important;color:#fff!important;font-size:11px!important;font-weight:900!important;text-decoration:none!important;cursor:pointer!important;width:100%!important}
      .mts-camera-btn.muted{background:rgba(255,255,255,.07)!important;border-color:rgba(255,255,255,.12)!important;color:#aaa!important}
      .mts-camera-modal{position:fixed!important;inset:0!important;z-index:99999!important;display:grid!important;place-items:center!important;padding:24px!important;background:rgba(0,0,0,.72)!important;backdrop-filter:blur(8px)!important}
      .mts-camera-dialog{width:min(980px,96vw)!important;max-height:92vh!important;display:grid!important;grid-template-rows:auto minmax(260px,1fr) auto!important;border:1px solid rgba(255,255,255,.12)!important;border-radius:18px!important;background:#101015!important;box-shadow:0 30px 90px rgba(0,0,0,.55)!important;overflow:hidden!important}
      .mts-camera-dialog-head,.mts-camera-dialog-foot{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:12px!important;padding:12px 14px!important;background:rgba(255,255,255,.045)!important}
      .mts-camera-dialog-head b{display:block!important;color:#fff!important;font-size:15px!important}.mts-camera-dialog-head span{display:block!important;color:#aaa!important;font-size:11px!important;word-break:break-all!important}
      .mts-camera-dialog-head button,.mts-camera-dialog-foot a{border:1px solid rgba(255,255,255,.12)!important;border-radius:10px!important;background:rgba(255,255,255,.08)!important;color:#fff!important;padding:8px 10px!important;font-size:12px!important;font-weight:900!important;text-decoration:none!important;cursor:pointer!important}
      .mts-camera-frame{display:grid!important;place-items:center!important;min-height:360px!important;background:#050506!important;overflow:auto!important}
      .mts-camera-frame img{max-width:100%!important;max-height:72vh!important;object-fit:contain!important}
      .mts-camera-frame small{color:#aaa!important;font-size:11px!important;margin-top:8px!important}
      .mts-machine-network{margin-top:12px;border:1px solid rgba(255,255,255,.08);border-radius:16px;background:rgba(0,0,0,.16);overflow:visible}
      .mts-machine-network summary{cursor:pointer;padding:11px 13px;color:#fff;font-weight:900;list-style:none}
      .mts-machine-network summary::-webkit-details-marker{display:none}
      .mts-machine-network-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;padding:0 12px 12px}
      .mts-machine-network-grid label{display:grid;gap:6px;color:#aaa;font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.04em}
      .mts-machine-network-grid input,.mts-machine-network-grid select{width:100%;border:1px solid rgba(255,255,255,.12);border-radius:12px;background:#101010;color:#fff;padding:10px 11px;font-family:'Fredoka',sans-serif;font-size:13px;outline:none}
      .mts-machine-network-actions{display:flex;flex-wrap:wrap;gap:8px;padding:0 12px 12px}
      .mts-machine-network-actions .mts-btn{padding:9px 12px;font-size:12px;width:auto!important}
      .mts-machine-network-status{margin:0;padding:0 12px 13px;color:#bbb;font-size:12px;line-height:1.45}
      .mts-connector-box{margin-top:12px;border:1px solid rgba(255,255,255,.08);border-radius:16px;background:rgba(255,255,255,.045);padding:12px 13px;color:#fff}
      .mts-connector-box.online{border-color:rgba(0,200,83,.25);background:rgba(0,200,83,.08)}
      .mts-connector-box.offline{border-color:rgba(255,43,43,.25);background:rgba(255,43,43,.08)}
      .mts-connector-box.muted{color:#aaa}
      .mts-connector-head{display:flex;justify-content:space-between;gap:10px;align-items:center}
      .mts-connector-head b{font-size:13px}.mts-connector-head span{color:#aaa;font-size:11px;font-weight:900}
      .mts-connector-box small{display:block;margin-top:6px;color:#cfcfcf;line-height:1.4}
      .mts-connector-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:10px}
      .mts-connector-grid span{border:1px solid rgba(255,255,255,.08);border-radius:12px;background:rgba(0,0,0,.14);padding:8px;font-size:12px;font-weight:900;text-align:center}
      @media(max-width:980px){.mts-machine-network-grid{grid-template-columns:1fr 1fr}}
      @media(max-width:760px){.mts-machine-network-grid{grid-template-columns:1fr}}
    `;
    document.head.appendChild(style);
  }

  function ensureContainer(){
    const panel = document.getElementById("mtsPanelProduction");
    const kanban = document.getElementById("mtsProductionKanban");
    if(!panel || !kanban) return null;
    let box = document.getElementById("mtsWorkshopPro");
    if(!box){
      box = document.createElement("div");
      box.id = "mtsWorkshopPro";
      box.className = "mts-workshop-pro";
      kanban.parentNode.insertBefore(box, kanban);
    }
    return box;
  }

  function collectMachinePatch(cardEl){
    const patch = {};
    cardEl.querySelectorAll("[data-machine-field]").forEach(field=>{
      patch[field.dataset.machineField] = String(field.value || "").trim();
      if(field.dataset.machineField === "localIp"){ patch.ip = patch.localIp; patch.host = patch.localIp; }
      if(field.dataset.machineField === "connectionType") patch.type = patch.connectionType;
    });
    return patch;
  }

  async function testConnection(machineId){
    const machine = cfg.state.machines.find(m=>m.id === machineId);
    if(!machine) return;

    const url = connectionTestUrl(machine);
    if(!url){
      await MTS.WorkshopMachines.saveMachine(machineId, {
        lastConnectionStatus:"offline",
        lastConnectionAt:h.nowIso(),
        lastConnectionMessage:"IP manquante. Renseigne l'adresse IP puis enregistre."
      });
      return;
    }

    await MTS.WorkshopMachines.saveMachine(machineId, {
      lastConnectionStatus:"testing",
      lastConnectionAt:h.nowIso(),
      lastConnectionMessage:"Test en cours..."
    });

    try{
      const controller = new AbortController();
      const timer = setTimeout(()=>controller.abort(), 4500);
      const res = await fetch(url, {method:"GET", cache:"no-store", signal:controller.signal});
      clearTimeout(timer);
      if(!res.ok) throw new Error(`Reponse HTTP ${res.status}`);

      let message = "Connexion reussie.";
      if(machine.connectionType === "klipper"){
        try{
          const data = await res.json();
          const state = data?.result?.state || data?.result?.hostname || "Moonraker detecte";
          message = `Connexion Klipper/Moonraker OK : ${state}`;
        }catch(e){
          message = "Connexion OK, reponse non JSON.";
        }
      }

      await MTS.WorkshopMachines.saveMachine(machineId, {
        lastConnectionStatus:"online",
        lastConnectionAt:h.nowIso(),
        lastConnectionMessage:message
      });
    }catch(err){
      const reason = String(err.message || err.name || "Erreur inconnue");
      const blockedHint = "Si l'interface s'ouvre dans un nouvel onglet mais que le test echoue, c'est surement le navigateur qui bloque le HTTP local depuis Shopify HTTPS.";
      await MTS.WorkshopMachines.saveMachine(machineId, {
        lastConnectionStatus:"offline",
        lastConnectionAt:h.nowIso(),
        lastConnectionMessage:`Test echoue : ${reason}. ${blockedHint}`
      });
    }
  }

  async function testViaStudio(machineId){
    const machine = cfg.state.machines.find(m=>m.id === machineId);
    if(!machine) return;

    await MTS.WorkshopMachines.saveMachine(machineId, {
      lastConnectionStatus:"testing",
      lastConnectionAt:h.nowIso(),
      lastConnectionMessage:"Lecture des donnees Connector en cours..."
    });

    const data = await MTS.WorkshopConnector.pollConnectorTelemetry();
    const found = data?.machines?.find(m=>h.uidSafe(m.id || m.name) === h.uidSafe(machineId));

    if(found){
      MTS.WorkshopMachines.applyConnectorTelemetry({machines:[found]});
      return;
    }

    await MTS.WorkshopMachines.saveMachine(machineId, {
      lastConnectionStatus:"offline",
      lastConnectionAt:h.nowIso(),
      lastConnectionMessage:"Aucune telemetrie Connector trouvee pour cette machine. Verifie que l'id du connector est identique, par exemple ender1."
    });
  }

  function bind(box){
    if(box.dataset.workshopBound === "1") return;
    box.dataset.workshopBound = "1";

    box.addEventListener("toggle", e=>{
      const details = e.target.closest?.("[data-network-details]");
      if(!details) return;
      const id = details.dataset.networkDetails;
      if(details.open) st.openNetworkPanels.add(id);
      else st.openNetworkPanels.delete(id);
    }, true);

    box.addEventListener("input", e=>{
      const field = e.target.closest?.("[data-machine-field]");
      if(!field) return;
      const cardEl = field.closest("[data-machine-id]");
      if(cardEl) updateMachineDraft(cardEl.dataset.machineId, collectMachinePatch(cardEl));
    });

    box.addEventListener("change", e=>{
      const field = e.target.closest?.("[data-machine-field]");
      if(!field) return;
      const cardEl = field.closest("[data-machine-id]");
      if(cardEl) updateMachineDraft(cardEl.dataset.machineId, collectMachinePatch(cardEl));
    });

    box.addEventListener("click", async e=>{
      const saveBtn = e.target.closest("[data-machine-save]");
      if(saveBtn){
        const cardEl = saveBtn.closest("[data-machine-id]");
        const id = saveBtn.dataset.machineSave;
        saveBtn.disabled = true;
        await MTS.WorkshopMachines.saveMachine(id, collectMachinePatch(cardEl));
        saveBtn.disabled = false;
        return;
      }

      const testBtn = e.target.closest("[data-machine-test]");
      if(testBtn){
        const cardEl = testBtn.closest("[data-machine-id]");
        const id = testBtn.dataset.machineTest;
        testBtn.disabled = true;
        await MTS.WorkshopMachines.saveMachine(id, collectMachinePatch(cardEl));
        await testConnection(id);
        testBtn.disabled = false;
        return;
      }

      const studioTestBtn = e.target.closest("[data-machine-studio-test]");
      if(studioTestBtn){
        const cardEl = studioTestBtn.closest("[data-machine-id]");
        const id = studioTestBtn.dataset.machineStudioTest;
        studioTestBtn.disabled = true;
        await MTS.WorkshopMachines.saveMachine(id, collectMachinePatch(cardEl));
        await testViaStudio(id);
        studioTestBtn.disabled = false;
        return;
      }

      const cameraDetectBtn = e.target.closest("[data-machine-camera-detect]");
      if(cameraDetectBtn){
        const id = cameraDetectBtn.dataset.machineCameraDetect;
        const machine = cfg.state.machines.find(m=>m.id === id);
        if(machine){
          st.cameraDetections.delete(id);
          cameraDetectBtn.disabled = true;
          await detectCamera(machine);
          cameraDetectBtn.disabled = false;
        }
        return;
      }

      const cameraOpenBtn = e.target.closest("[data-machine-camera-open]");
      if(cameraOpenBtn){
        openCameraPopup(cameraOpenBtn.dataset.machineCameraOpen, cameraOpenBtn.dataset.cameraUrl);
        return;
      }

      const statusBtn = e.target.closest("[data-machine-status]");
      if(statusBtn){
        const id = statusBtn.dataset.machineStatus;
        const value = statusBtn.dataset.statusValue || "";
        const patch = {
          manualStatus:value,
          status:value || "free",
          lastConnectionMessage:value === "maintenance" ? "Machine mise en maintenance manuellement." : value === "offline" ? "Machine forcee hors ligne manuellement." : "Etat manuel retire."
        };
        if(value === "offline") patch.lastConnectionStatus = "offline";
        statusBtn.disabled = true;
        await MTS.WorkshopMachines.saveMachine(id, patch);
        statusBtn.disabled = false;
      }
    });
  }

  async function render(){
    ensureStyles();
    const box = ensureContainer();
    if(!box) return;

    MTS.WorkshopMachines.listenMachines();
    MTS.WorkshopConnector.startPolling();

    if(!st.loaded){
      box.innerHTML = `<div class="mts-empty">Chargement de l'atelier...</div>`;
      await MTS.WorkshopMachines.loadMachines();
    }

    const prod = MTS.WorkshopProduction;
    const printers = MTS.WorkshopMachines.printerList();
    const active = prod.activeProjects();
    const free = printers.filter(p=>!prod.assignedProject(p)).length;
    const printing = active.filter(p=>p.status === "printing").length;
    const waiting = active.filter(p=>p.status === "paid").length;
    const noPrinter = prod.unassignedProjects();

    collectVisibleDrafts(box);

    box.innerHTML = `
      <div class="mts-workshop-head-v25">
        <div>
          <p class="mts-kicker">Atelier Pro V27.5</p>
          <h3>Parc imprimantes</h3>
          <p>Bibliotheque source de verite, production en lecture seule, Connector reserve a la telemetrie.</p>
        </div>
        <div class="mts-workshop-stats-v25">
          <span><b>${printers.length}</b> machines</span>
          <span><b>${free}</b> libres</span>
          <span><b>${printing}</b> impressions</span>
          <span><b>${waiting}</b> a lancer</span>
        </div>
      </div>

      <div class="mts-workshop-grid-v25">
        ${printers.map(card).join("")}
      </div>

      ${noPrinter.length ? `
        <div class="mts-workshop-unassigned">
          <strong>Projets actifs sans imprimante assignee</strong>
          <div>
            ${noPrinter.slice(0,6).map(p=>`<span>${h.esc(p.projectType || "Projet")} - ${h.esc(p.name || "Client")} - ${h.esc(prod.statusLabel(p))}</span>`).join("")}
          </div>
        </div>
      ` : ""}
    `;

    bind(box);
  }

  MTS.WorkshopUI = {
    render,
    bind,
    testConnection,
    testViaStudio,
    machineTelemetry,
    telemetryLine,
    connectionLabel,
    connectionDot
  };
})(window.MTS);
